import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
   AfterContentInit,
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   Input,
   OnChanges,
   OnDestroy,
   SimpleChanges,
   ViewChild,
} from '@angular/core';
import { EMPTY, merge, Subscription } from 'rxjs';

import { MatButton } from '@angular/material/button';
import { LcsDateTimePickerComponent } from '../date-time-picker/date-time-picker.component';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'lcs-date-time-picker-toggle',
    templateUrl: './date-time-picker-toggle.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        'class': 'lcs-date-time-picker-toggle',
        '[attr.tabindex]': 'disabled ? null : -1',
        '[class.lcs-date-time-picker-toggle-active]': 'dateTimePicker && dateTimePicker.opened',
        '[class.mat-accent]': 'dateTimePicker && dateTimePicker.color === "accent"',
        '[class.mat-warn]': 'dateTimePicker && dateTimePicker.color === "warn"',
        '(focus)': 'button.focus()',
    },
    exportAs: 'lcsDateTimePickerToggle',
    standalone: false
})
export class LcsDateTimePickerToggleComponent<D> implements AfterContentInit, OnChanges, OnDestroy {
   private stateChanges = Subscription.EMPTY;

   /** DateTimePicker instance that the button will toggle. */
   // eslint-disable-next-line @angular-eslint/no-input-rename
   @Input('for') dateTimePicker: LcsDateTimePickerComponent<D>;

   /** Whether the toggle button is disabled. */
   @Input()
   set disabled(value: boolean) {
      this._disabled = coerceBooleanProperty(value);
   }
   get disabled(): boolean {
      if (this._disabled === undefined && this.dateTimePicker) {
         return this.dateTimePicker.disabled;
      }

      return !!this._disabled;
   }
   private _disabled: boolean;

   /** Whether ripples on the toggle should be disabled. */
   @Input() disableRipple: boolean;

   /** Underlying button element. */
   @ViewChild('button') button: MatButton;

   constructor(private changeDetectorRef: ChangeDetectorRef) {}

   ngOnChanges(changes: SimpleChanges) {
      if (changes.dateTimePicker) {
         this.watchStateChanges();
      }
   }

   ngOnDestroy() {
      this.stateChanges.unsubscribe();
   }

   ngAfterContentInit() {
      this.watchStateChanges();
   }

   open(event: Event): void {
      if (this.dateTimePicker && !this.disabled) {
         this.dateTimePicker.open();
         event.stopPropagation();
      }
   }

   private watchStateChanges() {
      const dateTimePickerDisabled = this.dateTimePicker ? this.dateTimePicker.disabledChange.asObservable() : EMPTY;
      const inputDisabled =
         this.dateTimePicker && this.dateTimePicker.dateTimePickerInput
            ? this.dateTimePicker.dateTimePickerInput.disabledChange.asObservable()
            : EMPTY;
      const dateTimePickerToggled = this.dateTimePicker
         ? merge(this.dateTimePicker.openedStream, this.dateTimePicker.closedStream)
         : EMPTY;

      this.stateChanges.unsubscribe();
      this.stateChanges = merge(dateTimePickerDisabled, inputDisabled, dateTimePickerToggled).subscribe(() =>
         this.changeDetectorRef.markForCheck()
      );
   }

   /* eslint-disable @typescript-eslint/member-ordering */
   /* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match */
   static ngAcceptInputType_disabled: BooleanInput;
}
